import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CatalogItem } from 'banesco-catalogues-api';
import { BnCataloguesService } from 'services';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ContactsHttpService as ContactsAuxHttpService } from 'banesco-contact-aux-api';
import { AccountIdTypes } from '../constants/account-id-types';
import { NgSelectConfig } from '@ng-select/ng-select';
@Component({
  selector: 'bn-contact-other-form',
  templateUrl: './contact-other-form.component.html',
})
export class BnContactOtherFormComponent implements OnInit, OnDestroy {
  @Input() hostRef: any;
  @Input() optionalFields = true;
  @Input() largeTitles = false;
  @Input() bankTitle?: string;
  @Input() isTransfer?: boolean;
  @Input() allowedProductTypes?: string[];
  @Input() selectedProductType?: string;
  @Input() customLabels?: { accountNumber: string };
  @Output() formChanged = new EventEmitter();
  @Output() currencyChanged = new EventEmitter();
  @Output() dirtyStatusChange = new EventEmitter();
  destroy$ = new Subject<boolean>();
  infoTooltipText = $localize`:@@contact-manager.banesco-form.infoLabel:A short or friendly way to call your beneficiary`;
  selectIdTypeTooltip = $localize`:@@contact-manager.other-form.selectIDType:Select an ID type`;
  selectBankPlaceholder = $localize`:@@contact-manager.other-form.selectBank:Select a bank`;
  selectProductTypePlaceholder = $localize`:@@contact-manager.other-form.selectProductType:Select a product type`;
  selectCurrencyPlaceholder = $localize`:@@contact-manager.other-form.selectCurrency:Select a currency`;
  loadingValidation = false;
  idError = '';
  currencies: CatalogItem[] = [];
  idTypes: CatalogItem[] = [];
  banks: CatalogItem[] = [];
  productTypes: CatalogItem[] = [];
  otherBankForm = new FormGroup({
    idType: new FormControl('', Validators.required),
    idNumber: new FormControl({ value: '', disabled: true }, [Validators.required, this.idNumberValidator]),
    validId: new FormControl(false, [Validators.required, this.validIdValidator]),
    name: new FormControl(''),
    accountNumber: new FormControl('', [Validators.required, Validators.maxLength(42)]),
    bank: new FormControl(null, [Validators.required]),
    productType: new FormControl('', Validators.required),
    currency: new FormControl('', Validators.required),
    alias: new FormControl(),
    email: new FormControl('', Validators.email),
  });

  constructor(
    private catalogueService: BnCataloguesService,
    private contactsAuxHttpService: ContactsAuxHttpService,
    private config: NgSelectConfig
    ) {
      this.config.notFoundText = '';
    }

  ngOnInit(): void {
    this.getCurrencies()
      .pipe(take(1))
      .subscribe((data) => {
        this.currencies = data.map(({ id, desc }) => ({ id, desc: id ? `${id}$ - ${desc}` : '' }));
      });

    this.catalogueService
      .getBanks()
      .pipe(take(1))
      .subscribe((data) => {
        this.banks = data.filter((bank) => bank.id !== 'BankBL');
      });

    this.catalogueService
      .getProductTypes()
      .pipe(take(1))
      .subscribe((data) => {
        this.productTypes = this.allowedProductTypes
          ? data.filter((type) => !type.id || this.allowedProductTypes?.includes(type.id))
          : data;

        if (this.selectedProductType) {
          const selected = this.productTypes.find((type) => type.id === this.selectedProductType);
          this.otherBankForm.get('productType')?.setValue(selected);
        }
      });

    this.catalogueService
      .getIdTypes()
      .pipe(take(1))
      .subscribe((data) => {
        this.idTypes = data;
      });

    this.otherBankForm
      .get('idType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.otherBankForm.get('idNumber')?.enable({ emitEvent: false });
        this.otherBankForm.get('idNumber')?.patchValue('', { emitEvent: false });
        this.otherBankForm.get('idNumber')?.updateValueAndValidity({ emitEvent: false });
        this.otherBankForm.get('name')?.patchValue('');
        if (val.id === 'PAS') {
          this.otherBankForm.get('validId')?.setValue(true);
        }
        this.idError = '';
      });

    this.otherBankForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.dirtyStatusChange.emit(this.otherBankForm.dirty);
      this.currencyChanged.emit(this.otherBankForm.get('currency')?.value?.id);

      if (this.otherBankForm.valid) {
        this.formChanged.emit({
          contactsPostRequestBody: {
            accessContextScope: 'USER',
            name: val.name,
            category: 'local',
            alias: val.alias,
            emailId: val.email,
            additions: {
              idType: val.idType.id,
              idNumber: val.idNumber,
            },
            accounts: [
              {
                accountNumber: val.accountNumber.replace(/ /g, ''),
                bankName: val.bank.desc,
                bankCode: val.bank.id,
                additions: {
                  productType: val.productType.id,
                  currencyCode: val.currency.id,
                },
              },
            ],
          },
        });
      } else {
        this.formChanged.emit(undefined);
      }
    });

    this.initiateValidIdNumberValidator();
  }

  getCurrencies(): Observable<CatalogItem[]> {
    return this.isTransfer ? this.catalogueService.getLocalTransferCurrencies() : this.catalogueService.getCurrencies();
  }

  idNumberValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.parent!) {
      return null;
    }
    const idType = control.parent!.get('idType')?.value;
    if (control.value && idType) {
      const isnum = /^\d+$/.test(control.value);

      switch (idType.id) {
        case AccountIdTypes.CIP:
          if (!isnum || control.value.length !== 11) {
            return { cip: true };
          }
          break;
        case AccountIdTypes.RNC:
          if (!isnum || control.value.length !== 9) {
            return { rnc: true };
          }
          break;
        case AccountIdTypes.PAS:
          if (control.value.length < 4 || control.value.length > 15) {
            return { pas: true };
          }
          break;
      }
      return null;
    }
    return null;
  }

  initiateValidIdNumberValidator(): void {
    const idNumberValue$ = this.otherBankForm.get('idNumber')?.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      filter((value) => (this.isCipFullLength(value) || this.isRncFullLength(value)))
    );

    const validId$ = idNumberValue$?.pipe(
      tap((_) => {
        this.idError = '';
        this.otherBankForm.get('name')?.patchValue('');
        this.loadingValidation = true;
      }),
      switchMap((value) => {
        return this.contactsAuxHttpService
          .getContactByTypeId({
            contactauxservice: {
              ContactType: {
                TypeId: this.otherBankForm.get('idType')?.value?.id,
                IdContact: value,
              },
            },
          }).pipe(
            catchError((err) => {
              console.error(err);
              this.loadingValidation = false;
              this.otherBankForm.get('validId')?.setValue(false);
              this.otherBankForm.get('validId')?.updateValueAndValidity();
              if (err.error.message) {
                this.idError = err.error.message;
              } else {
                this.idError = $localize`:@@contact-manager.other-form.idError:There was an error with the validation service`;
              }
              this.otherBankForm.get('idNumber')?.setErrors({ invalidNumber: true });
              return of(null);
            })
          )
        }),
        tap((body) => {
          this.loadingValidation = false;
          this.otherBankForm.get('name')?.setValue(body?.ContactInformation?.Nombre);
          this.otherBankForm.get('validId')?.setValue(true);
          this.otherBankForm.get('validId')?.updateValueAndValidity();
        })
    );

    validId$?.subscribe();
  }

  validIdValidator(control: AbstractControl): ValidationErrors | null {
    return !control.value ? { error: true } : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  get idTypeMask(): string {
    let mask = '';
    const idType = this.otherBankForm.get('idType')?.value?.id;
    switch (idType) {
      case AccountIdTypes.CIP:
        mask = '000-0000000-0';
        break;
      case AccountIdTypes.RNC:
        mask = '0-00-00000-0';
        break;
    }
    return mask;
  }

  isRncFullLength(value: string) {
    return this.otherBankForm.get('idType')?.value?.id === AccountIdTypes.RNC && value.length === 9;
  }

  isCipFullLength(value: string) {
    return this.otherBankForm.get('idType')?.value?.id === AccountIdTypes.CIP && value.length === 11;
  }
}
